import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { getStoredSession } from '../services/api/sessionApi';
import { GREETING_ROLE_LABELS } from '../constants/staff';
import { submitReview, fetchOrgName } from '../services/api/reviewsApi';
import { ROUTES } from '../constants/routes';

/**
 * ReviewPage (REVIEW-FORM / R1, FE-1). "Rate ArogyaKhata" — any org member,
 * any role, may leave one in-app review. Binds to the FROZEN §C1 contract
 * (state/epics/REVIEW-FORM/build/build-plan.md); never to built backend code.
 *
 * Identity block is read-only, sourced from the session + one guarded org
 * name fetch (D7, see reviewsApi.fetchOrgName) — never sent back on the
 * write, the server stamps identity itself (D3/D5).
 */

const REVIEW_MIN_LEN = 20;
const REVIEW_MAX_LEN = 600;
const COUNTER_WARN_AT = 550;
const CONSENT_TEXT_V3 =
  "I consent to ArogyaKhata publishing this review on arogyakhata.com, attributed to my name and my "
  + "organisation's name. I may withdraw this consent at any time by contacting +91 88675 91155.";
const WHATSAPP_URL = 'https://wa.me/918867591155';
const WHATSAPP_DISPLAY = '+91 88675 91155';
const STARS = [1, 2, 3, 4, 5];

function getDisabledReason(rating, review, consentChecked) {
  if (!rating) return 'Pick a rating.';
  if (review.trim().length < REVIEW_MIN_LEN) return 'Write at least 20 characters.';
  if (!consentChecked) return 'Tick the consent box to submit.';
  return null;
}

export default function ReviewPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { role } = usePermissions();

  const session = getStoredSession();
  const userName = session?.displayName || session?.username || '';
  const roleLabel = GREETING_ROLE_LABELS[role] || 'User';

  // D7 — org name lives nowhere in the stored session. One guarded GET on
  // mount, ref-gated so re-renders can't fire it again (no request storm).
  const [orgName, setOrgName] = useState(null);
  const orgNameFetchRef = useRef(false);
  useEffect(() => {
    if (orgNameFetchRef.current) return;
    orgNameFetchRef.current = true;
    fetchOrgName().then((name) => setOrgName(name || null));
  }, []);

  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const inFlightRef = useRef(false);

  const handleReviewChange = (e) => {
    setReview(e.target.value.slice(0, REVIEW_MAX_LEN));
  };

  const disabledReason = getDisabledReason(rating, review, consent);

  const handleSubmit = useCallback(async () => {
    if (inFlightRef.current || disabledReason) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setErrorMsg(null);
    const res = await submitReview({
      rating,
      review: review.trim(),
      consent_publish: consent,
    });
    inFlightRef.current = false;
    setSubmitting(false);
    if (res.success) {
      setSubmitted(true);
    } else {
      setErrorMsg(res.error);
    }
  }, [rating, review, consent, disabledReason]);

  const bg = isDark ? 'bg-slate-900' : 'bg-slate-50';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';
  const textBody = isDark ? 'text-slate-100' : 'text-slate-900';

  if (submitted) {
    return (
      <div className={`min-h-screen ${bg}`} data-testid="review-page">
        <AdminPageHeader />
        <div className="mx-auto max-w-xl p-4">
          <div
            data-testid="thank-you-panel"
            className={`rounded-xl border p-6 text-center ${cardBg}`}
          >
            <div className="mb-2 text-3xl" aria-hidden="true">🙏</div>
            <p className={`text-base font-semibold ${textBody}`}>Thank you — your review is with us.</p>
            <button
              type="button"
              className="mt-4 min-h-11 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
              onClick={() => navigate(ROUTES.ORG)}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const counterClass = review.length >= COUNTER_WARN_AT
    ? 'text-amber-600 dark:text-amber-400'
    : textMuted;

  return (
    <div className={`min-h-screen ${bg}`} data-testid="review-page">
      <AdminPageHeader />
      <div className="mx-auto max-w-xl p-4">
        <h1 className={`mb-1 text-xl font-semibold ${textBody}`}>Rate ArogyaKhata</h1>
        <p className={`mb-4 text-sm ${textMuted}`}>
          Tell us how the app is working for your organisation.
        </p>

        {/* Identity block — read-only, server-stamped on submit. */}
        <div
          data-testid="identity-block"
          className={`mb-4 rounded-xl border p-4 text-sm ${cardBg} ${textBody}`}
        >
          <div data-testid="identity-org-name" className="font-medium">
            {orgName || 'Your organisation'}
          </div>
          <div className={`mt-0.5 ${textMuted}`}>
            {userName || 'You'} · {roleLabel}
          </div>
        </div>

        {/* Star rating */}
        <div className="mb-4">
          <div className={`mb-2 text-sm font-medium ${textBody}`}>How would you rate ArogyaKhata?</div>
          <div className="flex gap-1" data-testid="star-picker">
            {STARS.map((n) => (
              <button
                key={n}
                type="button"
                aria-label={n === 1 ? '1 star' : `${n} stars`}
                aria-pressed={rating === n}
                onClick={() => setRating(n)}
                className="min-h-11 min-w-11 rounded-lg text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <span aria-hidden="true">{n <= rating ? '★' : '☆'}</span>
              </button>
            ))}
          </div>
          {rating > 0 && rating <= 2 && (
            <p data-testid="low-rating-helper" className={`mt-2 text-sm ${textMuted}`}>
              Sorry it is not working well for you. You can also message us on WhatsApp{' '}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 underline dark:text-teal-400"
              >
                {WHATSAPP_DISPLAY}
              </a>.
            </p>
          )}
        </div>

        {/* Review text */}
        <div className="mb-4">
          <label htmlFor="review-text" className={`mb-2 block text-sm font-medium ${textBody}`}>
            Tell us more (at least 20 characters)
          </label>
          <textarea
            id="review-text"
            data-testid="review-textarea"
            value={review}
            onChange={handleReviewChange}
            maxLength={REVIEW_MAX_LEN}
            rows={5}
            placeholder="What's working well, and what could be better?"
            className={`w-full rounded-lg border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              isDark ? 'border-slate-600 bg-slate-900 text-slate-100' : 'border-slate-300 bg-white text-slate-900'
            }`}
          />
          <div data-testid="review-counter" className={`mt-1 text-right text-xs ${counterClass}`}>
            {review.length}/{REVIEW_MAX_LEN}
          </div>
        </div>

        {/* Consent checkbox — VERBATIM v3 copy, do not paraphrase. */}
        <label className={`mb-4 flex items-start gap-2 text-sm ${textBody}`}>
          <input
            type="checkbox"
            data-testid="consent-checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0"
          />
          <span>{CONSENT_TEXT_V3}</span>
        </label>

        {/* Error panel — ABOVE the button, typed text preserved. */}
        {errorMsg && (
          <div
            role="alert"
            data-testid="error-panel"
            className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
          >
            <p>{errorMsg}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid="retry-button"
                onClick={handleSubmit}
                className="min-h-11 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
              >
                Try again
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-rose-700 underline dark:text-rose-200"
              >
                Message us on WhatsApp instead
              </a>
            </div>
          </div>
        )}

        <button
          type="button"
          data-testid="submit-button"
          disabled={submitting || !!disabledReason}
          onClick={handleSubmit}
          className="min-h-11 w-full rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-teal-600 disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Submit review'}
        </button>
        {!submitting && disabledReason && (
          <p data-testid="disabled-reason" className={`mt-2 text-center text-sm ${textBody}`}>
            {disabledReason}
          </p>
        )}
      </div>
    </div>
  );
}
